import { posthog, useFeatureFlagEnabled } from '@/app-shell/flags/posthog-compat';
import { z } from 'zod';

/**
 * Gates the pricing / change-plan modal and the View all plans entry points.
 * PostHog (GRO — rollout reliability), matching the sibling billing gate
 * `IS_BILLING_CUSTOM_DOWNGRADE_FLOWS_ENABLED`. Rollout gate, default OFF.
 *
 * A hook rather than a `posthog.isFeatureEnabled` thunk: every consumer is a
 * render path, and the thunk does not re-render when the flags land.
 */
export const useIsHeidi2PricingPlansModalEnabled = () =>
  useFeatureFlagEnabled('release-heidi-2-pricing-plans-modal') ?? false;

/**
 * Gates Heidi 2 Clinician/Practice **Max** upgrade CTAs. PostHog, default OFF.
 * Standard checkout is live, so free/trial users keep their Standard CTA
 * regardless.
 */
export const useIsHeidi2FullUpgradeFlowEnabled = () =>
  useFeatureFlagEnabled('release-heidi-2-full-upgrade-flow') ?? false;

/**
 * Sells Practice Max (MONEY-1128). PostHog, default OFF. ON adds the
 * Standard/Max toggle to the Practice column of the pricing plans modal, routes
 * the Practice Standard and Clinician Max upgrade entries to Practice Max, and
 * restores the sidebar credit-usage tile for Clinician Max seats (GRO-4552 hid
 * it while they had nothing to upgrade to). Recognising an existing Practice
 * Max seat (name, overage behaviour) is not gated.
 */
export const useIsHeidi2PracticeMaxUpgradeEnabled = () =>
  useFeatureFlagEnabled('release-heidi-2-practice-max-upgrade') ?? false;

const HEIDI_2_TEAM_UPGRADES_FLAG = 'release-heidi-2-team-upgrades';

/**
 * Heidi 2 team upgrades: the Practice seat card and the Scribe Team
 * Practice Standard card in the pricing plans modal, the
 * `/settings/billing/upgrade` seat checkout, and the Members table Seat column.
 * PostHog, default OFF.
 */
export const useIsHeidi2TeamUpgradesEnabled = () =>
  useFeatureFlagEnabled(HEIDI_2_TEAM_UPGRADES_FLAG) ?? false;

/**
 * {@link useIsHeidi2TeamUpgradesEnabled} without the `false` fallback:
 * `undefined` until the flags load, so a route can wait instead of bouncing.
 */
export const useHeidi2TeamUpgradesFlagState = (): boolean | undefined =>
  useFeatureFlagEnabled(HEIDI_2_TEAM_UPGRADES_FLAG);

const HEIDI_2_MEMBER_UPGRADE_REQUEST_FLAG = 'release-heidi-2-member-upgrade-request';

/**
 * Heidi 2 member upgrade requests: the seat request modal a non-admin team
 * member reaches from the upgrade entry points, and the Max seat dialog the
 * request email opens for a Practice admin on Members. PostHog, default OFF;
 * the PostHog flag depends on `release-heidi-2-team-upgrades`.
 */
export const useIsHeidi2MemberUpgradeRequestEnabled = () =>
  useFeatureFlagEnabled(HEIDI_2_MEMBER_UPGRADE_REQUEST_FLAG) ?? false;

/**
 * {@link useIsHeidi2MemberUpgradeRequestEnabled} without the `false` fallback:
 * `undefined` until the flags load, so a host can wait instead of bouncing.
 */
export const useHeidi2MemberUpgradeRequestFlagState = (): boolean | undefined =>
  useFeatureFlagEnabled(HEIDI_2_MEMBER_UPGRADE_REQUEST_FLAG);

/**
 * Kill switch: ON pauses scheduling Heidi 2 team upgrades (the seat checkout's
 * Schedule action) while billing is reconciled. PostHog, default OFF. ml-scribe
 * reads the same key and refuses the save, so this only explains the refusal.
 */
export const useIsHeidi2TeamUpgradeSchedulingDisabled = () =>
  useFeatureFlagEnabled('disable-heidi-2-team-upgrade-scheduling') ?? false;

/**
 * Mirrors ml-scribe `features/release/enable_agent_credit_allowance_gate.py`
 * (same PostHog key, same Kinde distinct_id). Gates the Clinician Work
 * lockout — grey Send, skipped create/turn POSTs, hidden follow-ups — so
 * FE and BE refuse together or not at all. Rollout gate, fail-open.
 *
 * Two-flag matrix (cohort = `release-heidi-2-opt-in` AND `heidi_version === 2`):
 * - cohort off × gate * : no banners, no lockout (cohort chrome off)
 * - cohort on × gate off: banners/GET still live; Send stays enabled at 100
 * - cohort on × gate on : Clinician lockout at remaining <= 0 (Max complimentary still sends)
 * Flip the gate off first to unblock Work; do not enable the gate wider than
 * the emit cohort. Key is inherited from ml-scribe — do not rename it here.
 */
export const useIsAgentCreditAllowanceGateEnabled = () =>
  useFeatureFlagEnabled('enable-agent-credit-allowance-gate') ?? false;

export const IS_HIDE_PRO_MONTHLY_OPTION = () => {
  return posthog.isFeatureEnabled('release-hide-pro-monthly-option');
};

/** PostHog: custom downgrade flows in billing (retention offer / cancel confirmation). */
export const IS_BILLING_CUSTOM_DOWNGRADE_FLOWS_ENABLED = () => {
  return posthog.isFeatureEnabled('release-billing-custom-downgrade-flows-enabled');
};

export const IS_GAMIFY_TRIAL_LENGTH_ENABLED = () => {
  return posthog.isFeatureEnabled('release-gamify-trial-length');
};

export const IS_GAMIFY_TRIAL_LENGTH_CLINICIAN_ENABLED = () => {
  return posthog.isFeatureEnabled('release-gamify-trial-length-clinician');
};

/** PostHog multivariate: CONTROL (un-skippable), OPTIONAL (skippable), NONE (no modal). */
export type ClinicianTrialModalExperimentVariant = 'CONTROL' | 'OPTIONAL' | 'NONE';

export const CLINICIAN_TRIAL_MODAL_EXPERIMENT_FLAG_KEY =
  'exp-scribe-clinician-trial-modal';

/**
 * True when PostHog returned a multivariate variant string (user is in the experiment).
 * `false` / `undefined` / `null` mean the flag is off or the user is outside rollout — not enrolled.
 */
export const IS_CLINICIAN_TRIAL_MODAL_EXPERIMENT_ASSIGNED = (): boolean => {
  const raw = posthog.getFeatureFlag(CLINICIAN_TRIAL_MODAL_EXPERIMENT_FLAG_KEY);
  return typeof raw === 'string';
};

export const GET_CLINICIAN_TRIAL_MODAL_EXPERIMENT_VARIANT =
  (): ClinicianTrialModalExperimentVariant => {
    const schema = z.enum(['CONTROL', 'OPTIONAL', 'NONE']).catch('CONTROL');
    return schema.parse(
      posthog.getFeatureFlag(CLINICIAN_TRIAL_MODAL_EXPERIMENT_FLAG_KEY)
    );
  };

export const IS_END_OF_90_DAY_TRIAL_EXPERIENCE_ENABLED = () => {
  return posthog.isFeatureEnabled('release-end-of-90-day-trial-experience');
};
